import * as React from "react";
import get from "lodash/get";
import omit from "lodash/omit";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm";

import "./LoyaltyProgressBar.css";
import { ComponentBase, ComponentBaseProps } from "../ComponentBase";
import { ProgressBarFill } from "../ProgressBarFill";
import {
  getIsMemberTierLoading,
  getMemberTierCustomer,
} from "../../reducers/methods";
import { interpolate } from "../../utils/string";
import { currencySymbols } from "../../utils/currencies";
import { formatUserFacingNumber } from "../../utils/number";

/**
 * ENH-376 — Loyalty Progress Bar (shopper-facing render).
 *
 * One component covering all three design variants (design.md D11): the
 * variants share the whole state-resolution question and differ only in which
 * elements they draw, so splitting them would triplicate that logic.
 *
 * Field and enum names below are fixed by design.md's wire-contract appendix —
 * a sibling build in `fbt-retailer-app` writes the config this reads, so
 * `variant`, `style.fillColor`, `style.trackColor`, `markerImage.src`,
 * `progressText`, `topTierMessage`, the four `suppress*` toggles and the
 * `testMode` values must not drift.
 */

export type LoyaltyProgressBarVariant = "card" | "inline" | "compact";

/**
 * The shopper states this component resolves to. The first eight are also the
 * merchant-facing preview values (`testMode`) fixed by the wire contract.
 *
 * `absent` is internal and has no preview: it is the "there is nothing to draw"
 * outcome shared by a merchant with no member tier program, one whose program
 * was never enabled and one whose program is archived
 * (REQ-LPB-052/053/054) — all of which reach the browser as a record carrying
 * no tier data at all.
 */
export type LoyaltyProgressBarState =
  | "climbing"
  | "topTier"
  | "unreachable"
  | "tierAssignment"
  | "nonMember"
  | "noRemainder"
  | "loading"
  | "failure"
  | "absent";

/**
 * Merchant style settings. `fillColor` and `trackColor` are this component's
 * own keys because a progress bar needs two independent colours, where every
 * other block in the builder has at most one (design.md D3, wire contract).
 *
 * `badgeBackgroundColor`/`badgeTextColor` carry REQ-LPS-008/009. They are NOT
 * named in the wire contract — see the build report; they follow the contract's
 * own convention of literal colour keys on `style`.
 */
export interface LoyaltyProgressBarStyle extends React.CSSProperties {
  fillColor?: string;
  trackColor?: string;
  badgeBackgroundColor?: string;
  badgeTextColor?: string;
  tierBadgeColors?: LoyaltyProgressBarTierBadgeColors;
}

/**
 * Per-tier badge colours, keyed by member tier id.
 *
 * A tier absent from this map falls back to `badgeBackgroundColor`/
 * `badgeTextColor`. That fallback is doing two jobs at once: it is the answer
 * to "what does a tier the merchant has not styled look like", and it is why
 * every widget saved before this map existed renders exactly as it did.
 *
 * Keyed by id rather than by name because Member Tier settings can rename a
 * tier, and the badge prints that name — a rename has to keep its colours, not
 * orphan them.
 */
export interface LoyaltyProgressBarTierBadgeColors {
  [memberTierId: string]: {
    backgroundColor?: string;
    textColor?: string;
  };
}

/**
 * The `style` keys this component consumes itself rather than handing to its
 * container node — the two bar colours, the bar's height and roundness, and the
 * badge's two colours.
 */
const BAR_STYLE_KEYS = [
  "fillColor",
  "trackColor",
  "badgeBackgroundColor",
  "badgeTextColor",
  // Must stay listed: `render` spreads every unlisted `style` key onto the
  // container as an inline CSS property, and this one is an object.
  "tierBadgeColors",
  "height",
  "borderRadius",
];

export interface LoyaltyProgressBarMarkerImage {
  src?: string;
}

/**
 * Builder-only. The tier the merchant currently has selected in the Tier Badge
 * settings, so the preview badge can stand for that tier while they colour it.
 * Never written by the storefront, which reads the shopper's real tier.
 */
export interface LoyaltyProgressBarBadgePreviewTier {
  id?: string;
  name?: string;
}

export interface LoyaltyProgressBarProps extends ComponentBaseProps {
  classNames: string;
  label: string;
  style?: LoyaltyProgressBarStyle;
  variant?: LoyaltyProgressBarVariant;

  /** Merchant-editable copy (REQ-LPS-011/012). Interpolated per REQ-LPS-013/014/066/067/068. */
  progressText?: string;
  topTierMessage?: string;

  /** Merchant-uploaded image riding the leading edge of the fill (REQ-LPB-036). */
  markerImage?: LoyaltyProgressBarMarkerImage;

  /** Which tier the preview badge stands for. Builder canvas only. */
  badgePreviewTier?: LoyaltyProgressBarBadgePreviewTier;

  /** Element suppression toggles (REQ-LPS-015/016/017/018). */
  suppressBadge?: boolean;
  suppressHeadline?: boolean;
  suppressProgressText?: boolean;
  suppressEndLabels?: boolean;

  isLoading: ReturnType<typeof getIsMemberTierLoading>;
  memberTierCustomer: ReturnType<typeof getMemberTierCustomer>;

  /**
   * `variables.nextTier.unavailableReason`, read off the raw customer record by
   * the container. The reducer does not map it, and deliberately is not changed
   * to (design.md D7/D13) — `TierUpgradeRequirement` reads the same field the
   * same way today.
   */
  unavailableReason?: string;

  /** Set by the container once the fetch has outlived its budget (REQ-LPB-041). */
  hasRequestTimedOut?: boolean;

  /** Merchant preview switcher, edit mode only (design.md D6). */
  testMode?: string;
}

/** REQ-LPB-057 — the shipped default before a merchant edits it. */
export const DEFAULT_PROGRESS_TEXT =
  "Earn {{memberTier.needed}} to unlock {{memberTier.next}}";

/** REQ-LPB-058. */
export const DEFAULT_TOP_TIER_MESSAGE = "You've reached our highest tier";

/** REQ-LPB-059. */
export const NON_MEMBER_BADGE_TEXT = "Not a member";

/**
 * Dwightbot round 3 — fbt-membertier's own synthetic name for a member
 * below the lowest configured tier, not a real tier a merchant named.
 * `fbt-membertier/local_modules/constants.ts:14`'s `NO_TIER_NAME`, sent
 * verbatim as `memberTierNameCached` and mapped straight to `current`
 * (`getMemberTierCustomer.ts` reducer's `current: data.memberTierNameCached`)
 * with no normalization anywhere in between. A non-empty, truthy string,
 * so every `!record.current`-style guard here silently let it through:
 * REQ-LPB-027 ("no badge at all rather than an invented label") rendered
 * a badge reading literally "no tier" for this cohort — the largest one
 * in the seeded data, not an edge case.
 */
const RAW_NO_TIER_NAME = "no tier";

/** The record values this component draws from, once flattened. */
interface MemberTierRecordView {
  current?: string;
  /**
   * The id of the tier `current` names. Only the badge reads it, to find that
   * tier's colours; everything else here is display data.
   */
  memberTierId?: string;
  next?: string;
  points?: number;
  needed?: number;
  /**
   * Whether the response carried a top-level `nextTier` object at all. This —
   * not `unavailableReason` — is what separates a member genuinely at the top
   * tier from one whose next tier is merely unreachable (design.md D13).
   */
  hasNextTierRecord: boolean;
  /**
   * The program's display currency/unit. For a purchase-based program this
   * is already the real currency code (e.g. "USD") — `formatExternalMemberTierCustomer`
   * formats it identically whether read from here or from `variables.points.u`,
   * since both trace back to the same `pointsCurrency()`/`pointsOrCurrency()`
   * ternary on `ledgerCurrency` (fbt-membertier/libs/util/valueFormatter.ts).
   * No separate "purchasePointsCurrency" field exists on the wire.
   */
  pointsCurrency?: string;
  /**
   * A purchase-based member tier program measures spend rather than points.
   * `pointsCurrency` alone can't signal this — for a purchase-based program
   * it already equals the real currency code, and no sentinel value like
   * "friendbuyPurchasePoints" is ever sent (that's an internal
   * `ledgerCurrency` value, never the formatted `pointsCurrency`). The
   * only reliable signal on the wire is the type tag on the formatted
   * points value (Dwightbot round 1, finding 3).
   */
  isCurrency?: boolean;
}

/**
 * Sample records behind the merchant's preview switcher. Figures follow exhibit
 * `progress-bar-states.html` so a merchant previewing a state sees the
 * arrangement the exhibit shows.
 */
const PREVIEW_RECORDS: { [state: string]: MemberTierRecordView } = {
  climbing: {
    current: "Gold Tier",
    next: "Platinum Tier",
    points: 200,
    needed: 100,
    hasNextTierRecord: true,
    pointsCurrency: "points",
  },
  topTier: {
    current: "Platinum Tier",
    points: 640,
    hasNextTierRecord: false,
    pointsCurrency: "points",
  },
  unreachable: {
    current: "Silver Tier",
    next: "Gold Tier",
    points: 200,
    needed: 300,
    hasNextTierRecord: true,
    pointsCurrency: "points",
  },
  tierAssignment: {
    current: "Gold Tier",
    next: "Platinum Tier",
    points: 200,
    hasNextTierRecord: true,
    pointsCurrency: "points",
  },
  nonMember: {
    next: "Bronze Tier",
    points: 0,
    needed: 100,
    hasNextTierRecord: true,
    pointsCurrency: "points",
  },
  noRemainder: {
    current: "Gold Tier",
    next: "Platinum Tier",
    points: 200,
    hasNextTierRecord: true,
    pointsCurrency: "points",
  },
  loading: { hasNextTierRecord: false },
  failure: { hasNextTierRecord: false },
};

/**
 * The badge's two colours for the tier a member holds: that tier's own pair
 * when the merchant has styled it, and otherwise the single pair that styles
 * every tier.
 *
 * The per-key fallback matters as much as the per-tier one. A merchant who
 * sets only a background for Gold keeps the shared text colour rather than
 * losing it to `undefined`, so a half-filled entry cannot produce an
 * unreadable badge.
 */
function resolveBadgeColors(
  style: LoyaltyProgressBarStyle | undefined,
  memberTierId: string | undefined
): { backgroundColor?: string; color?: string } | undefined {
  if (!style) {
    return undefined;
  }

  // Array path, not a dotted string: a tier id is merchant data and has no
  // business being parsed as a lodash path.
  const tierColors = memberTierId
    ? get(style, ["tierBadgeColors", memberTierId])
    : undefined;

  return {
    backgroundColor:
      get(tierColors, "backgroundColor") || style.badgeBackgroundColor,
    color: get(tierColors, "textColor") || style.badgeTextColor,
  };
}

/** Number-ish record values arrive as numbers or numeric strings. */
function toFiniteNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  const parsed = Number(value);

  return isFinite(parsed) ? parsed : undefined;
}

/**
 * Whether the record carries nothing this component could draw: no tier held,
 * and no next tier on the wire.
 *
 * Extracted because two branches of the resolver below depend on it — the
 * timeout branch and `absent` — and they have to agree. If only one of them
 * learns about a future change to what "no tier data" means (the
 * `RAW_NO_TIER_NAME` mapping in `getRecord` is the likely place), a bar that
 * has a record starts resolving to `failure` again and disappears off the page
 * mid-session.
 */
function hasNoTierData(record: MemberTierRecordView): boolean {
  return !record.current && !record.hasNextTierRecord;
}

export interface ResolveStateInput {
  isCustomerAuthenticated: boolean;
  isLoading: boolean;
  hasRequestTimedOut: boolean;
  record: MemberTierRecordView;
  unavailableReason?: string;
}

/**
 * The whole state machine, in the order design.md D13's resolution table fixes.
 *
 * Exported so it can be exercised directly — every branch below is a
 * requirement, and several are unreachable through the rendered output alone.
 */
export function resolveLoyaltyProgressBarState(
  input: ResolveStateInput
): LoyaltyProgressBarState {
  const {
    isCustomerAuthenticated,
    isLoading,
    hasRequestTimedOut,
    record,
    unavailableReason,
  } = input;

  // A visitor who is not an identified loyalty member (REQ-LPB-029/030/031).
  // Checked first because no record is ever fetched for one — every loyalty
  // component in this SDK gates its dispatch on authentication.
  if (!isCustomerAuthenticated) {
    return "nonMember";
  }

  // REQ-LPB-041: an unretrievable record renders nothing. The container reports
  // the fetch outliving its budget; a fetch that failed outright leaves the
  // slice empty, which lands on `absent` below — both render nothing, so the
  // two need no separating.
  //
  // Gated on there being nothing to draw, which is what "unretrievable" means.
  // `isLoading` is one slice-wide flag shared by every member-tier-dispatching
  // container in this SDK, and the container re-arms its budget on each
  // false->true transition — so a bar that already has its record can be handed
  // a timeout belonging to a *sibling's* fetch. Reachable here rather than
  // theoretical: this widget's own seed deliberately exposes the full Loyalty
  // palette (REQ-LPS-054/055), so a merchant who adds any other loyalty block
  // puts a second dispatcher on this slice.
  //
  // Established by reading the code and pinned by the two tests named for this
  // case, NOT observed in a browser -- forcing a real fetch past its budget on
  // demand was not something this could be reproduced against. Honouring the
  // flag unconditionally resolves a populated bar to `failure`, which renders
  // null: the bar leaves the page mid-session and the layout reflows around
  // the gap, the outcome REQ-LPB-039's placeholder exists to prevent. A record
  // that is already drawable outranks a budget that ran out elsewhere.
  if (hasRequestTimedOut && hasNoTierData(record)) {
    return "failure";
  }

  // REQ-LPB-039/040. Note this is NOT gated on `hasNoTierData` the way the
  // branch above is: a bar holding a record still shows the placeholder while
  // any container's fetch is in flight, so the same shared-flag flicker
  // survives here in a milder form. Left deliberately — changing it alters
  // what every ordinary refetch renders, which is a wider behaviour change
  // than the disappearance above and wants its own decision.
  if (isLoading) {
    return "loading";
  }

  // No tier data of any kind: no program, never enabled, or archived
  // (REQ-LPB-052/053/054), and equally a fetch that failed.
  if (hasNoTierData(record)) {
    return "absent";
  }

  // design.md D13, row 1 — top-level `nextTier` absent is the only signal that
  // identifies a genuine top-tier member (REQ-LPB-014/015/016/017).
  if (!record.hasNextTierRecord) {
    return "topTier";
  }

  // D13 row 2 (REQ-LPB-019).
  if (unavailableReason === "tierAssignment") {
    return "tierAssignment";
  }

  // D13 row 3 — unreachable by earning, which is NOT the top tier
  // (REQ-LPB-080, and REQ-LPB-022 withholds the top-tier message).
  if (unavailableReason === "maxTier") {
    return "unreachable";
  }

  // REQ-LPB-023 — a next tier with no remainder has no denominator to draw
  // against. Deliberately after the two unavailable cases, which have their own
  // no-remainder handling.
  if (record.needed === undefined) {
    return "noRemainder";
  }

  // D13 row 4 (REQ-LPB-001). A member holding their tier through an extension
  // and one just past a points reset both arrive here and are drawn from their
  // real figure, which is REQ-LPB-081 and the reset half of REQ-LPB-032/033 —
  // the response does not distinguish them and, per the exhibit, need not.
  return "climbing";
}

/** States that draw the bar itself. */
function rendersBar(state: LoyaltyProgressBarState): boolean {
  return (
    state === "climbing" ||
    state === "topTier" ||
    state === "unreachable" ||
    state === "nonMember"
  );
}

/**
 * The filled proportion, 0..1.
 *
 * REQ-LPB-001: progress figure over the next tier's threshold, where the
 * threshold is `points + needed` — `pointsRequired` is computed server-side as
 * `rule.minPoints - points`, so the two sum back to the threshold. Measured
 * from zero on both points- and purchase-based programs (REQ-LPB-067).
 */
export function resolveFilledProportion(
  state: LoyaltyProgressBarState,
  record: MemberTierRecordView
): number {
  // REQ-LPB-014 — a genuine top-tier member's bar is full.
  if (state === "topTier") {
    return 1;
  }

  // REQ-LPB-029 — a visitor who is not an identified member sees an empty bar.
  if (state === "nonMember") {
    return 0;
  }

  const points = record.points === undefined ? 0 : record.points;
  const needed = record.needed;

  // REQ-LPB-080. An unreachable next tier still carries a real `pointsRequired`
  // whenever the tier also has an unmet points rule, and then the ordinary
  // proportion is the honest one. Only when no remainder exists at all is there
  // no denominator to divide by, and the bar fills (design.md D13).
  if (needed === undefined) {
    return 1;
  }

  const threshold = points + needed;

  if (threshold <= 0) {
    return 1;
  }

  // REQ-LPB-049 — a figure past the threshold clamps at one rather than
  // overflowing the track. REQ-LPB-032 needs no special handling: a decreased
  // figure simply produces a smaller proportion.
  return Math.max(0, Math.min(1, points / threshold));
}

export class LoyaltyProgressBar extends ComponentBase<LoyaltyProgressBarProps> {
  onMarkerImageError = () => {
    // REQ-LPB-038 — an image that cannot be loaded leaves the leading edge bare
    // rather than showing a broken-image glyph.
    this.setState({ markerImageFailed: true });
  };

  componentDidUpdate(prevProps: LoyaltyProgressBarProps) {
    // CodeRabbit, PR 1: this override shadowed ComponentBase's own
    // componentDidUpdate entirely -- 4478ca6 added this method where none
    // existed before, with no super call, silently killing horizontal
    // alignment, edit-mode invisible-layer/corner-box cleanup, the
    // mousedown-listener swap between edit and preview mode, form-error
    // re-validation, and edit-panel boundary checks for every instance of
    // this component. A regression from that commit, not a pre-existing
    // gap -- no Phase 5 proof row covered alignment.
    super.componentDidUpdate(prevProps);

    // Phase 5 proof, live-executed: same one-way-latch shape as 2597c93's
    // hasRequestTimedOut fix in the container -- markerImageFailed was set
    // on error and never cleared, so a merchant fixing a broken marker URL
    // left shoppers already on the page permanently without one until a
    // full reload remounted the component. Clear it whenever the src
    // actually changes; a genuinely new src deserves its own onError
    // chance regardless of how the last one ended.
    const prevSrc = get(prevProps.markerImage, "src");
    const currentSrc = get(this.props.markerImage, "src");

    if (prevSrc !== currentSrc && this.state.markerImageFailed) {
      this.setState({ markerImageFailed: false });
    }
  }

  /**
   * In edit mode the merchant's preview switcher stands in for the record, so
   * every state can be shown on a canvas that has no shopper (design.md D6).
   * Mirrors `TierUpgradeRequirement`'s `isEditing` branch.
   */
  getRecord = (): MemberTierRecordView => {
    const { badgePreviewTier, isEditing, memberTierCustomer, testMode } =
      this.props;

    if (isEditing) {
      const preview =
        PREVIEW_RECORDS[testMode || "climbing"] || PREVIEW_RECORDS.climbing;

      // The merchant styles one tier at a time, so the preview badge has to be
      // that tier -- otherwise they colour Bronze and watch a Gold badge
      // ignore them. Guarded on `preview.current` because the non-member state
      // deliberately has no tier to stand for (REQ-LPB-031/059), and the
      // loading and failure states have no badge at all.
      if (badgePreviewTier && badgePreviewTier.name && preview.current) {
        return {
          ...preview,
          current: badgePreviewTier.name,
          memberTierId: badgePreviewTier.id,
        };
      }

      return preview;
    }

    const rawCurrent = get(memberTierCustomer, "current");

    return {
      current: rawCurrent === RAW_NO_TIER_NAME ? undefined : rawCurrent,
      memberTierId: get(memberTierCustomer, "memberTierId"),
      next: get(memberTierCustomer, "next"),
      points: toFiniteNumber(get(memberTierCustomer, "points")),
      needed: toFiniteNumber(get(memberTierCustomer, "needed")),
      // The top-level `nextTier` object, not `variables.nextTier` — see D13.
      hasNextTierRecord: Boolean(get(memberTierCustomer, "nextTier")),
      pointsCurrency: get(memberTierCustomer, "pointsCurrency"),
      isCurrency:
        get(memberTierCustomer, "variables.points.t") === "currency",
    };
  };

  getState = (record: MemberTierRecordView): LoyaltyProgressBarState => {
    const {
      data,
      hasRequestTimedOut,
      isEditing,
      isLoading,
      memberTierCustomer,
      testMode,
      unavailableReason,
    } = this.props;

    if (isEditing) {
      const previewed = testMode || "climbing";

      return PREVIEW_RECORDS[previewed]
        ? (previewed as LoyaltyProgressBarState)
        : "climbing";
    }

    return resolveLoyaltyProgressBarState({
      isCustomerAuthenticated: get(data, "customer.authenticated", false),
      isLoading: Boolean(isLoading),
      hasRequestTimedOut: Boolean(hasRequestTimedOut),
      record,
      unavailableReason:
        unavailableReason ||
        get(memberTierCustomer, "variables.nextTier.unavailableReason", ""),
    });
  };

  /**
   * Whether the program measures money rather than points, taken from the
   * member tier program itself and never from a merchant setting
   * (REQ-LPB-065, REQ-LPS-046).
   */
  isPurchaseBased = (record: MemberTierRecordView): boolean =>
    record.isCurrency === true;

  /**
   * A figure as the shopper reads it: an amount in the program's purchase
   * points currency (REQ-LPB-012/013), or a number labelled with the program's
   * points currency (REQ-LPB-063/064).
   *
   * `withLabel` is off for the end labels, which the exhibit renders bare on a
   * points program because the axis they sit on already says what they count.
   */
  formatFigure = (
    value: number | undefined,
    record: MemberTierRecordView,
    withLabel: boolean = true
  ): string => {
    if (value === undefined) {
      return "";
    }

    // REQ-LPB-035/047 ask for the merchant account's language. No merchant
    // locale reaches this SDK — see the build report — so this is the house
    // number formatter every other loyalty component uses.
    const formatted = formatUserFacingNumber(value);

    if (this.isPurchaseBased(record)) {
      // pointsCurrency IS the real currency code for a purchase-based
      // program (see MemberTierRecordView.pointsCurrency) — there is no
      // separate purchasePointsCurrency field on the wire.
      const code = record.pointsCurrency;
      const symbol = code ? get(currencySymbols, code, code) : "";

      return `${symbol}${formatted}`;
    }

    return withLabel && record.pointsCurrency
      ? `${formatted} ${record.pointsCurrency}`
      : formatted;
  };

  /**
   * The data merchant-edited copy is interpolated against.
   *
   * Five placeholders, per the wire contract. Four map straight off the reducer
   * slice; `threshold` is computed here as `points + needed` because the API
   * carries no absolute threshold (REQ-LPS-068).
   *
   * The `memberTier` values are overridden with their formatted forms so a
   * merchant's `{{memberTier.needed}}` reads "100 points", not "100"
   * (REQ-LPB-064). The override is local to this component's copy; the raw
   * slice other components interpolate against is untouched.
   */
  getInterpolationData = (record: MemberTierRecordView) => {
    const { data } = this.props;
    const threshold =
      record.points === undefined || record.needed === undefined
        ? undefined
        : record.points + record.needed;

    return {
      ...(data as object),
      memberTier: {
        ...get(data, "memberTier", {}),
        current: record.current || "",
        next: record.next || "",
        needed: this.formatFigure(record.needed, record),
        points: this.formatFigure(record.points, record),
        threshold: this.formatFigure(threshold, record),
      },
    };
  };

  renderMarkdown = (text: string) => (
    <div className="LoyaltyProgressBar-text">
      <span className="Text-markdown-container">
        <ReactMarkdown skipHtml={true} linkTarget="_blank" plugins={[gfm]}>
          {text}
        </ReactMarkdown>
      </span>
    </div>
  );

  renderBadge = (
    state: LoyaltyProgressBarState,
    record: MemberTierRecordView
  ) => {
    const { suppressBadge, style, variant = "card" } = this.props;

    // REQ-LPB-069 gives the badge to the Card variant; REQ-LPB-074 withholds it
    // from Compact, and the Inline variant carries the tier pair instead.
    if (variant !== "card") {
      return null;
    }

    // REQ-LPS-015.
    if (suppressBadge) {
      return null;
    }

    const isNonMember = state === "nonMember";
    // REQ-LPB-031/059 — no member tier name for a visitor who is not a member,
    // placeholder wording instead. REQ-LPB-027 — a member below the lowest tier
    // gets no badge at all rather than an invented label.
    const text = isNonMember ? NON_MEMBER_BADGE_TEXT : record.current;
    const badgeStyle = resolveBadgeColors(style, record.memberTierId);

    return (
      <div className="LoyaltyProgressBar-badgeRow">
        {text ? (
          <span
            className={`LoyaltyProgressBar-badge ${
              isNonMember ? "LoyaltyProgressBar-badge-nonMember" : ""
            }`}
            style={isNonMember ? undefined : badgeStyle}
          >
            {text}
          </span>
        ) : null}
      </div>
    );
  };

  /**
   * The headline figure. It is always the quantity the bar is drawn from, so
   * the two can never disagree (REQ-LPB-006/007).
   */
  renderHeadline = (
    state: LoyaltyProgressBarState,
    record: MemberTierRecordView
  ) => {
    const { suppressHeadline, variant = "card" } = this.props;

    // REQ-LPB-070 gives it to Card; REQ-LPB-073 and REQ-LPB-075 withhold it
    // from Inline and Compact.
    if (variant !== "card" || suppressHeadline) {
      return null;
    }

    // REQ-LPB-051 — zero for a visitor who is not an identified member.
    const points = state === "nonMember" ? 0 : record.points;

    if (points === undefined) {
      return null;
    }

    const isPurchase = this.isPurchaseBased(record);

    return (
      <p className="LoyaltyProgressBar-headline">
        {this.formatFigure(points, record, false)}
        {!isPurchase && record.pointsCurrency ? (
          <span className="LoyaltyProgressBar-headlineUnit">
            {" "}
            {record.pointsCurrency}
          </span>
        ) : null}
      </p>
    );
  };

  /** REQ-LPB-071/072 — the Inline variant's current/next pair. */
  renderTierPair = (
    state: LoyaltyProgressBarState,
    record: MemberTierRecordView
  ) => {
    const { variant = "card" } = this.props;

    if (variant !== "inline" || !record.current) {
      return null;
    }

    // REQ-LPB-072 — a member with no next tier is named alone, so the arrow
    // never points at nothing. Per the exhibit the no-bar states name the
    // current tier alone as well (REQ-LPB-078): with no progress drawn toward
    // the next tier, an arrow pointing at it says something the component is
    // not showing. A tier the member cannot reach is still one whose bar is
    // drawn, so `unreachable` keeps the pair.
    const showsNext =
      rendersBar(state) && state !== "topTier" && Boolean(record.next);

    return (
      <p className="LoyaltyProgressBar-tierPair">
        {record.current}
        {showsNext ? (
          <React.Fragment>
            <span className="LoyaltyProgressBar-tierPairArrow">&rarr;</span>
            {record.next}
          </React.Fragment>
        ) : null}
      </p>
    );
  };

  renderProgressText = (
    state: LoyaltyProgressBarState,
    record: MemberTierRecordView
  ) => {
    const {
      progressText = DEFAULT_PROGRESS_TEXT,
      suppressProgressText,
      topTierMessage = DEFAULT_TOP_TIER_MESSAGE,
      variant = "card",
    } = this.props;

    // REQ-LPB-076 — Compact carries no progress text.
    if (variant === "compact") {
      return null;
    }

    // REQ-LPB-015 — the top-tier message stands in for the progress text, which
    // REQ-LPB-016 withholds. REQ-LPB-022 keeps the message away from the
    // unreachable case, which is not the top tier.
    if (state === "topTier") {
      return topTierMessage ? this.renderMarkdown(topTierMessage) : null;
    }

    // REQ-LPB-024 — no bar, no progress text.
    if (!rendersBar(state)) {
      return null;
    }

    // REQ-LPS-017.
    if (suppressProgressText) {
      return null;
    }

    // REQ-LPB-004 gates the progress text on having a next member tier. For the
    // unreachable case the tier exists and is named, so the text renders — the
    // one thing withheld there is the top-tier message (REQ-LPB-022). Whether a
    // shopper should be told to earn toward a tier they cannot reach is an open
    // product question recorded in design.md D13, not settled here.
    if (!record.next) {
      return null;
    }

    // The remainder is the one value the shipped copy cannot do without, and
    // the unreachable case can arrive with a named next tier but no
    // `pointsRequired` at all (design.md D13). A sentence carrying an unfilled
    // `{{memberTier.needed}}` is worse than no sentence, so it is withheld.
    if (record.needed === undefined) {
      return null;
    }

    const interpolated = interpolate(
      progressText,
      this.getInterpolationData(record)
    );

    return interpolated ? this.renderMarkdown(interpolated) : null;
  };

  renderBar = (
    state: LoyaltyProgressBarState,
    record: MemberTierRecordView
  ) => {
    const { markerImage, style } = this.props;
    const proportion = resolveFilledProportion(state, record);
    const percent = proportion * 100;
    const markerSrc = get(markerImage, "src");
    // REQ-LPB-037/038 — no configured image, or one that failed to load, leaves
    // the leading edge bare. REQ-LPB-036 is unconditional otherwise: a
    // configured, loadable image renders at the leading edge even at 0% --
    // `left: 0%` is well-defined, so there's no case left to exclude it.
    const showsMarker = Boolean(markerSrc) && !this.state.markerImageFailed;

    // design.md D18 — shared with components/ProgressBar. trackClassNames/
    // fillClassNames carry this component's own CSS class names through so
    // LoyaltyProgressBar.css's existing defaults, transition and narrow-width
    // media query (REQ-LPB-060) keep applying unchanged.
    return (
      <ProgressBarFill
        displayType="linear"
        proportion={proportion}
        fillColor={get(style, "fillColor")}
        trackColor={get(style, "trackColor")}
        borderRadius={get(style, "borderRadius")}
        height={get(style, "height")}
        // The track was always full-width via normal block flow (no width
        // was ever set, inline or in CSS) -- ProgressBarFill's own default
        // ("500px", the reward bar's original fixed size) must not apply
        // here.
        width="100%"
        trackClassNames="LoyaltyProgressBar-track"
        fillClassNames="LoyaltyProgressBar-fill"
        overlay={
          showsMarker ? (
            <img
              className="LoyaltyProgressBar-marker"
              src={markerSrc}
              style={{ left: `${percent}%` }}
              onError={this.onMarkerImageError}
              alt=""
            />
          ) : null
        }
      />
    );
  };

  renderEndLabels = (
    state: LoyaltyProgressBarState,
    record: MemberTierRecordView
  ) => {
    const { suppressEndLabels } = this.props;

    // REQ-LPB-017 — a top-tier member's bar carries no end labels, and
    // REQ-LPS-018 lets a merchant drop them everywhere else.
    if (state === "topTier" || suppressEndLabels) {
      return null;
    }

    const isPurchase = this.isPurchaseBased(record);
    const points = state === "nonMember" ? 0 : record.points;
    const threshold =
      points === undefined || record.needed === undefined
        ? undefined
        : points + record.needed;

    // REQ-LPB-066/068 — a purchase-based program shows what the shopper has
    // spent, an achievement that only ever grows. REQ-LPB-002 — a points
    // program shows zero, because a balance moves both ways.
    const leftLabel = isPurchase
      ? `${this.formatFigure(points, record)} spent`
      : "0";

    // REQ-LPB-003, and REQ-LPB-050 for a visitor who is not a member.
    const rightLabel = this.formatFigure(threshold, record, false);

    // CodeRabbit, PR 1: an undefined threshold (nonMember, whose record
    // carries no real tier data) drew "0" on the left and nothing on the
    // right -- an artifact of the fallback math, not REQ-LPB-050, which was
    // withdrawn rather than replaced and so specifies no end labels for this
    // visitor at all. Purely defensive: if the sign-in gate matches the
    // member gate as REQ-LPB-029 requires, nonMember never reaches render()
    // in the first place (see finding-signin-vs-member-gate-mismatch.md);
    // this only guards the incomplete-row shape itself, whatever produces it.
    if (threshold === undefined) {
      return null;
    }

    return (
      <div className="LoyaltyProgressBar-ends">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
    );
  };

  /**
   * REQ-LPB-039/043 — a neutral placeholder holding the shape the loaded
   * component occupies, so the page does not reflow, carrying no value from the
   * record (REQ-LPB-040).
   */
  renderLoading = () => {
    const { style, variant = "card" } = this.props;
    const trackHeight = get(style, "height", 16);

    return (
      <React.Fragment>
        {variant === "card" ? (
          <React.Fragment>
            <div className="LoyaltyProgressBar-badgeRow">
              <span className="LoyaltyProgressBar-skeleton LoyaltyProgressBar-skeleton-badge" />
            </div>
            <div className="LoyaltyProgressBar-skeleton LoyaltyProgressBar-skeleton-headline" />
          </React.Fragment>
        ) : null}
        {variant !== "compact" ? (
          <div className="LoyaltyProgressBar-skeleton LoyaltyProgressBar-skeleton-text" />
        ) : null}
        <div
          className="LoyaltyProgressBar-skeleton LoyaltyProgressBar-skeleton-track"
          style={{
            height: trackHeight,
            borderRadius: get(style, "borderRadius"),
          }}
        />
        <div className="LoyaltyProgressBar-skeleton LoyaltyProgressBar-skeleton-ends" />
      </React.Fragment>
    );
  };

  renderBody = (
    state: LoyaltyProgressBarState,
    record: MemberTierRecordView
  ) => {
    if (state === "loading") {
      return this.renderLoading();
    }

    const showsBar = rendersBar(state);

    return (
      <React.Fragment>
        {this.renderTierPair(state, record)}
        {this.renderBadge(state, record)}
        {this.renderHeadline(state, record)}
        {this.renderProgressText(state, record)}
        {showsBar ? this.renderBar(state, record) : null}
        {/* REQ-LPB-025 — no bar, no end labels. */}
        {showsBar ? this.renderEndLabels(state, record) : null}
      </React.Fragment>
    );
  };

  render() {
    const {
      classNames,
      label = "loyalty_progress_bar",
      style,
      variant = "card",
    } = this.props;
    const record = this.getRecord();
    const state = this.getState(record);

    // REQ-LPB-041/042 — an unretrievable record renders nothing at all: no
    // message, no retry, no empty box. REQ-LPB-052/053/054 — the same for a
    // merchant with no member tier program to draw.
    if (state === "failure" || state === "absent") {
      return null;
    }

    // REQ-LPB-079 — with no bar, the Compact variant has nothing else to fall
    // back on, so it removes itself. REQ-LPB-026 (Card keeps the headline) and
    // REQ-LPB-078 (Inline keeps the tier name) are handled in their renderers.
    if (variant === "compact" && state !== "loading" && !rendersBar(state)) {
      return null;
    }

    // The bar-specific keys are consumed by the bar and the badge; everything
    // else the merchant set — spacing, colour, alignment, width — belongs to
    // the container, exactly as `MemberTier` and `TierUpgradeRequirement`
    // spread their `style` (design.md D3).
    const containerStyle = omit(style, BAR_STYLE_KEYS) as React.CSSProperties;

    return (
      <div
        id={this.props.configName}
        onDragStart={this.onDragStart}
        onMouseDown={this.onMouseDown}
        onAnimationEnd={this.resetAnimation}
        className={`LoyaltyProgressBar-container LoyaltyProgressBar-${variant} ${
          classNames || ""
        } ${this.state.animationClass}`}
        style={containerStyle}
        aria-label={label}
        ref={(component) => (this.component = component)}
      >
        {this.renderBody(state, record)}
      </div>
    );
  }
}
